// Conversation store — one entry per (agent_id) holding the loaded
// conversation + its messages + an active ActionCable subscription.
// On modal open we load the messages once + subscribe; on close we
// unsubscribe (saves a socket per open agent).

import { createStore } from "./store"
import { api } from "../lib/api"
import { subscribeToConversation } from "../lib/actioncable"

export const conversationStore = createStore({
  byAgent: {}, // { [agentId]: { conversation, messages, sending, unsubscribe } }
})

function patchAgent(agentId, patch) {
  conversationStore.setState((s) => ({
    ...s,
    byAgent: { ...s.byAgent, [agentId]: { ...(s.byAgent[agentId] || {}), ...patch } },
  }))
}

export async function openConversation(agentId) {
  const existing = conversationStore.getState().byAgent[agentId]
  if (existing && existing.subscribed) return existing

  const { conversation } = await api.get(`/api/v1/agents/${agentId}/conversation`)
  const { messages } = await api.get(`/api/v1/agents/${agentId}/conversation/messages`)

  const unsubscribe = subscribeToConversation(conversation.id, {
    onMessage: (payload) => appendMessage(agentId, payload),
  })

  patchAgent(agentId, { conversation, messages, sending: false, subscribed: true, unsubscribe })
  return conversationStore.getState().byAgent[agentId]
}

export function closeConversation(agentId) {
  const entry = conversationStore.getState().byAgent[agentId]
  if (entry && entry.unsubscribe) entry.unsubscribe()
  patchAgent(agentId, { subscribed: false, unsubscribe: null })
}

function appendMessage(agentId, payload) {
  conversationStore.setState((s) => {
    const entry = s.byAgent[agentId] || { messages: [] }
    if (entry.messages.some((m) => m.id === payload.id)) return s
    return {
      ...s,
      byAgent: {
        ...s.byAgent,
        [agentId]: { ...entry, messages: [...entry.messages, payload] },
      },
    }
  })
}

export async function sendMessage(agentId, text) {
  patchAgent(agentId, { sending: true })
  try {
    await api.post(`/api/v1/agents/${agentId}/conversation/messages`, { message: { content: text } })
    // The new row will appear via the ActionCable broadcast — no manual append.
  } finally {
    patchAgent(agentId, { sending: false })
  }
}

// POST /api/v1/agents/:id/conversation/clear — writes a [CHAT_RESET]
// marker on the server. The marker row arrives via the ActionCable
// stream like any other message. The UI uses `visibleMessages()` to
// filter out everything <= the most recent reset marker.
export async function clearChat(agentId) {
  patchAgent(agentId, { sending: true })
  try {
    await api.post(`/api/v1/agents/${agentId}/conversation/clear`, {})
  } finally {
    patchAgent(agentId, { sending: false })
  }
}

// Given an array of conversation messages (oldest → newest), return
// only the slice after the most recent [CHAT_RESET] marker. If no
// reset exists, returns the input as-is.
export function visibleMessages(messages) {
  if (!messages || messages.length === 0) return messages || []
  let cut = -1
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].kind === "system" && messages[i].content === "[CHAT_RESET]") {
      cut = i
      break
    }
  }
  return cut === -1 ? messages : messages.slice(cut + 1)
}
